import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import styles from "./UtilityDetailsPage.module.scss";
import Button from "../../../../common/components/xepp/button/Button";
import { BpsPageHeader } from "../../../../common/components/bps-admin/page-header/BpsPageHeader";
import { BpsTabs } from "../../../../common/components/bps-admin/tabs/BpsTabs";
import { UtilityProfileForm, UtilityProfileFormValues } from "./components/UtilityProfileForm";
import { UtilityPhonePreview } from "./components/UtilityPhonePreview";
import { UtilityFeatureConfiguration, UtilityFeatureFlags } from "./components/UtilityFeatureConfiguration";
import { UtilityEmailTemplateConfiguration } from "./components/UtilityEmailTemplateConfiguration";
import { UtilityApiKeysConfiguration } from "./components/UtilityApiKeysConfiguration";
import CommonLoadingSpinner from "../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import {
	useCreateUtilityConfigurationMutation,
	useEditUtilityConfigurationMutation,
	useGetUtilityConfigurationQuery,
	useGetUtilityCountriesQuery,
	useGetUtilityCurrenciesQuery,
} from "../../../../service/utility-configuration-service";
import { UTILITY_LANGUAGE_CODES } from "../../../../model/bps-admin/utility-configuration";
import {
	useGetEmailTemplateConfigurationQuery,
	useSaveEmailTemplateConfigurationMutation,
	useUploadEmailTemplateAssetMutation,
} from "../../../../service/email-template-configuration-service";
import {
	EmailTemplateAssetType,
	EmailTemplateFormValues,
} from "../../../../model/bps-admin/email-template-configuration";
import {
	useGetUtilityApiKeysQuery,
	useSaveUtilityApiKeysMutation,
} from "../../../../service/utility-api-keys-service";
import { UtilityApiKeysConfiguration as ApiKeysValues } from "../../../../model/bps-admin/utility-api-keys";
import { getErrorMessage } from "../../../../utils/error-handler";
import { ApplicationRoutes } from "../../../../routes";

const FORM_ID = "utility-details-form";

const TABS = [
	{ id: "profile", label: "Profile" },
	{ id: "features", label: "Feature Configuration" },
];

// Only for an existing utility — there is no email branding to edit on create.
const EMAIL_TEMPLATE_TAB = { id: "email-template", label: "Email Template" };

// Only for an existing utility too — its keys live in its own databases, provisioned after create.
const API_KEYS_TAB = { id: "api-keys", label: "API Keys" };

const UtilityDetailsPage = () => {
	const navigate = useNavigate();
	// Absent on the "New Utility" route — that is what puts the page in create mode.
	const { id } = useParams<{ id: string }>();
	const [activeTab, setActiveTab] = useState(TABS[0].id);
	// Kept here rather than in the tab: switching tabs unmounts it.
	const [featureFlags, setFeatureFlags] = useState<UtilityFeatureFlags>({
		customerFeatureFlags: [],
		adminFeatureFlags: [],
	});

	const { data: countries = [], isLoading: isLoadingCountries } = useGetUtilityCountriesQuery();
	const { data: currencies = [], isLoading: isLoadingCurrencies } = useGetUtilityCurrenciesQuery();
	const { data: details, isLoading: isLoadingDetails } = useGetUtilityConfigurationQuery(id ?? "", {
		skip: !id,
	});
	const [createUtility, { isLoading: isCreating }] = useCreateUtilityConfigurationMutation();
	const [editUtility, { isLoading: isEditing }] = useEditUtilityConfigurationMutation();
	// Skipped on create: the email branding belongs to a utility that already exists.
	const { data: emailTemplateConfiguration, isLoading: isLoadingEmailTemplate } =
		useGetEmailTemplateConfigurationQuery(id ?? "", { skip: !id });
	const [uploadEmailTemplateAsset, { isLoading: isUploadingEmailAsset }] = useUploadEmailTemplateAssetMutation();
	const [saveEmailTemplate, { isLoading: isSavingEmailTemplate }] = useSaveEmailTemplateConfigurationMutation();
	// Skipped on create: the keys live in the utility's own databases, which only exist once
	// it has been created and provisioned.
	const { data: apiKeys, isLoading: isLoadingApiKeys } = useGetUtilityApiKeysQuery(id ?? "", { skip: !id });
	const [saveApiKeys, { isLoading: isSavingApiKeys }] = useSaveUtilityApiKeysMutation();

	const isSaving = isCreating || isEditing || isUploadingEmailAsset || isSavingEmailTemplate || isSavingApiKeys;

	// isLoading, not isFetching: only the first load has nothing to show. A background
	// refetch of already-cached data would otherwise blank a filled-in form.
	// The details query is skipped in create mode, so it never holds the page there — but
	// the lookups do, since the country and currency selects have no options until they land.
	const isLoadingForm =
		isLoadingDetails || isLoadingCountries || isLoadingCurrencies || isLoadingEmailTemplate || isLoadingApiKeys;

	const {
		control,
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<UtilityProfileFormValues>({
		defaultValues: {
			name: "",
			currencyId: "",
			acronym: "",
			subDomain: "",
			logo: null,
			primaryColor: "#081F5C",
			secondaryColor: "#FA1515",
			accentColor: "#FACC15",
			countryId: "",
			language: "en",
		},
	});

	useEffect(() => {
		if (!details) return;

		const { utility } = details;

		reset({
			name: utility.utilityName,
			currencyId: utility.currency.id,
			acronym: utility.acronym,
			subDomain: utility.subDomain,
			// A URL rather than a File: the existing logo, kept unless a new file is picked.
			logo: utility.logo,
			primaryColor: utility.primaryColor,
			secondaryColor: utility.secondaryColor,
			accentColor: utility.accentColor,
			countryId: utility.country.id,
			language: UTILITY_LANGUAGE_CODES[utility.language],
		});

		setFeatureFlags({
			customerFeatureFlags: details.customerFeatures.map(feature => feature.flagsMithCode),
			adminFeatureFlags: details.adminFeatures.map(feature => feature.flagsMithCode),
		});
	}, [details, reset]);

	// Its own form: it is saved through its own endpoint, and only when something in it changed.
	const {
		control: emailTemplateControl,
		reset: resetEmailTemplate,
		getValues: getEmailTemplateValues,
		formState: { isDirty: isEmailTemplateDirty },
	} = useForm<EmailTemplateFormValues>({
		defaultValues: {
			logoEnabled: true,
			bannerEnabled: true,
			logo: null,
			logoText: null,
			banner: null,
			primaryColor: "",
			secondaryColor: "",
			accentColor: "",
			fontColor: "",
		},
	});

	useEffect(() => {
		if (!emailTemplateConfiguration) return;

		resetEmailTemplate({
			logoEnabled: emailTemplateConfiguration.logoEnabled,
			bannerEnabled: emailTemplateConfiguration.bannerEnabled,
			logo: emailTemplateConfiguration.logoAssetUrl,
			logoText: emailTemplateConfiguration.logoTextAssetUrl,
			banner: emailTemplateConfiguration.bannerAssetUrl,
			primaryColor: emailTemplateConfiguration.primaryColor ?? "",
			secondaryColor: emailTemplateConfiguration.secondaryColor ?? "",
			accentColor: emailTemplateConfiguration.accentColor ?? "",
			fontColor: emailTemplateConfiguration.fontColor ?? "",
		});
	}, [emailTemplateConfiguration, resetEmailTemplate]);

	// Its own form, like the email template: saved through its own endpoint, and only when changed.
	const {
		control: apiKeysControl,
		reset: resetApiKeys,
		getValues: getApiKeysValues,
		formState: { isDirty: isApiKeysDirty },
	} = useForm<ApiKeysValues>({
		defaultValues: {
			billingKey: "",
			vendingKey: "",
			paymentHubKey: "",
			adoraIntegrationsKey: "",
			billingVendorId: "",
			vendingVendorId: "",
		},
	});

	useEffect(() => {
		if (apiKeys) resetApiKeys(apiKeys);
	}, [apiKeys, resetApiKeys]);

	// Picked images are uploaded only here, on Save: an upload overwrites the image that live
	// emails already point at, so doing it on pick would publish an unsaved change.
	const saveEmailTemplateChanges = async (utilityId: string) => {
		const values = getEmailTemplateValues();
		const toAssetUrl = async (image: File | string | null, assetType: EmailTemplateAssetType) =>
			image instanceof File
				? (await uploadEmailTemplateAsset({ utilityId, assetType, file: image }).unwrap()).url
				: image;

		const configuration = {
			logoEnabled: values.logoEnabled,
			bannerEnabled: values.bannerEnabled,
			logoAssetUrl: await toAssetUrl(values.logo, "Logo"),
			logoTextAssetUrl: await toAssetUrl(values.logoText, "LogoText"),
			bannerAssetUrl: await toAssetUrl(values.banner, "Banner"),
			primaryColor: values.primaryColor || null,
			secondaryColor: values.secondaryColor || null,
			accentColor: values.accentColor || null,
			fontColor: values.fontColor || null,
		};
		await saveEmailTemplate({ utilityId, configuration }).unwrap();
	};

	// Drives the live phone preview.
	const [primaryColor, secondaryColor, accentColor, logo, acronym] = useWatch({
		control,
		name: ["primaryColor", "secondaryColor", "accentColor", "logo", "acronym"],
	});

	const onSubmit = async (values: UtilityProfileFormValues) => {
		try {
			if (id) {
				// The unchanged logo comes back as its URL; only a File is worth uploading.
				await editUtility({
					...values,
					...featureFlags,
					id,
					logo: values.logo instanceof File ? values.logo : null,
				}).unwrap();
				if (isEmailTemplateDirty) await saveEmailTemplateChanges(id);
				if (isApiKeysDirty) await saveApiKeys({ utilityId: id, configuration: getApiKeysValues() }).unwrap();
				toast.success(`${values.name} updated`);
				// Stays on the form: the utility being edited is still the thing being
				// worked on, and the toast is the confirmation.
			} else {
				// The field is required, so this only guards the File | string | null type.
				if (!(values.logo instanceof File)) return;

				await createUtility({ ...values, ...featureFlags, logo: values.logo }).unwrap();
				toast.success(`${values.name} created`);
				navigate(`/${ApplicationRoutes.ADMIN_BASE}/${ApplicationRoutes.BPS_UTILITY_SETUP}`);
			}

			
		} catch (error: any) {
			// The backend rejects a duplicate acronym or subdomain with a message worth showing.
			toast.error(getErrorMessage(error?.data) ?? `Could not ${id ? "update" : "create"} the utility`);
		}
	};

	return (
		<div className={styles.page}>
			<BpsPageHeader
				title="Utility Details"
				onBack={() => navigate(-1)}
				action={
					<Button
						text={id ? "Save Changes" : "Add Utility"}
						// Not a submit button bound to the form: the form only exists while the
						// Profile tab is open, and saving has to work from either tab. An invalid
						// field is on that tab, so a failed validation switches back to it.
						onClick={handleSubmit(onSubmit, () => setActiveTab(TABS[0].id))}
						disabled={isLoadingForm || isSaving}
					/>
				}
			/>

			<BpsTabs
				tabs={id ? [...TABS, EMAIL_TEMPLATE_TAB, API_KEYS_TAB] : TABS}
				activeTabId={activeTab}
				onTabChange={setActiveTab}
			/>

			{/* Replaces the whole tab area rather than each column: on edit, every tab renders
			    values that only exist once the utility has been fetched. */}
			<CommonLoadingSpinner loading={isLoadingForm} color="var(--app-primary)">
				<div className={styles.content}>
					{activeTab === "profile" && (
						<>
							<div className={styles.formColumn}>
								<UtilityProfileForm
									formId={FORM_ID}
									control={control}
									register={register}
									errors={errors}
									onSubmit={handleSubmit(onSubmit)}
									countries={countries}
									currencies={currencies}
									isSubDomainLocked={Boolean(id)}
								/>
							</div>
							<div className={styles.previewColumn}>
								<UtilityPhonePreview
									primaryColor={primaryColor}
									secondaryColor={secondaryColor}
									accentColor={accentColor}
									logo={logo}
									acronym={acronym}
								/>
							</div>
						</>
					)}

					{activeTab === "features" && (
						<div className={styles.featuresColumn}>
							<UtilityFeatureConfiguration value={featureFlags} onChange={setFeatureFlags} />
						</div>
					)}

					{activeTab === EMAIL_TEMPLATE_TAB.id && (
						<div className={styles.featuresColumn}>
							<UtilityEmailTemplateConfiguration
								control={emailTemplateControl}
								companyName={details?.utility.utilityName ?? ""}
							/>
						</div>
					)}

					{activeTab === API_KEYS_TAB.id && (
						<div className={styles.featuresColumn}>
							<UtilityApiKeysConfiguration control={apiKeysControl} />
						</div>
					)}

					{/* Sits over whichever tab is open, so the entered values stay visible
					    while the save is in flight but nothing can be edited. */}
					{isSaving && (
						<div className={styles.savingOverlay}>
							<BounceLoader color="var(--app-primary)" />
						</div>
					)}
				</div>
			</CommonLoadingSpinner>
		</div>
	);
};

export default UtilityDetailsPage;
